/* eslint-disable react/prop-types */
/**
 * Note companion dialogs — extracted VERBATIM from App.js (Dashboard) as
 * V2 Notes migration Phase 3B-2 Step 2 (Jun 2026).
 *
 * IMPORTANT: pure mechanical lift, same pattern as NoteDetailDialog.jsx.
 * All state and handlers still live in the legacy Dashboard and are passed
 * through each component's ctx bag. Do NOT add local state here without
 * checking the Dashboard call sites in App.js. A future V2 note host can
 * mount these natively by providing the same ctx contracts.
 */
import axios from "axios";
import { toast } from "sonner";
import { Ban } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { DebouncedTextarea } from "../../ui/debounced-textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

/** Signature Confirmation Dialog — sign as provider/supervisor + next-status pick. */
export const SignatureConfirmDialog = ({ ctx }) => {
  const {
    API,
    refreshNotes,
    refreshTodos,
    selectedNote,
    setShowNoteDetail,
    setShowSignatureConfirm,
    setSignWithoutDate,
    setSignatureDate,
    showSignatureConfirm,
    signWithoutDate,
    signatureAction,
    signatureDate,
    updateNoteStatusOptimistically,
    user,
  } = ctx;

  return (
      <Dialog open={showSignatureConfirm} onOpenChange={setShowSignatureConfirm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sign Note as {signatureAction === 'provider' ? 'Provider' : 'Supervisor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Signature Preview */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-3">Signature Preview:</h3>
              <div className="bg-white border border-gray-200 rounded p-3 italic text-gray-800">
                <p className="font-semibold text-lg">{user.full_name || `${user.first_name} ${user.last_name}`}</p>
                {user.credentials && <p className="text-sm text-gray-600">{user.credentials}</p>}
                {!signWithoutDate && signatureDate && (
                  <p className="text-xs text-gray-500 mt-2">
                    Signed: {new Date(signatureDate).toLocaleDateString()} at {new Date(signatureDate).toLocaleTimeString()}
                  </p>
                )}
                {signWithoutDate && <p className="text-xs text-gray-500 mt-2">Signed (date not recorded)</p>}
              </div>
            </div>

            {/* Date/Time Options */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="signWithoutDate"
                  checked={signWithoutDate}
                  onChange={(e) => setSignWithoutDate(e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <Label htmlFor="signWithoutDate">Sign without date/time</Label>
              </div>

              {!signWithoutDate && (
                <div>
                  <Label>Signature Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={signatureDate ? new Date(signatureDate).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setSignatureDate(new Date(e.target.value).toISOString())}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">You can backdate or future-date the signature if needed</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 mt-6">
              <p className="text-sm font-medium text-gray-700 mb-2">What would you like to do next?</p>

              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={async () => {
                  try {
                    const signatureData = signatureAction === 'provider' ? {
                      provider_signed_at: signWithoutDate ? null : signatureDate,
                      provider_signed_by: user.id,
                      status: 'draft'
                    } : {
                      supervisor_signed_at: signWithoutDate ? null : signatureDate,
                      supervisor_signed_by: user.id,
                      status: 'draft'
                    };

                    await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
                    toast.success(`Signed as ${signatureAction} - kept as Draft`);
                    refreshNotes(); refreshTodos();
                    setShowSignatureConfirm(false);
                    // Reset signature state
                    setSignatureDate(new Date().toISOString());
                    setSignWithoutDate(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to sign note');
                  }
                }}
              >
                📝 Keep as Draft
              </Button>

              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={async () => {
                  try {
                    const signatureData = signatureAction === 'provider' ? {
                      provider_signed_at: signWithoutDate ? null : signatureDate,
                      provider_signed_by: user.id,
                      status: 'completed'
                    } : {
                      supervisor_signed_at: signWithoutDate ? null : signatureDate,
                      supervisor_signed_by: user.id,
                      status: 'completed'
                    };

                    await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
                    toast.success(`Signed and marked as Completed`);
                    refreshNotes(); refreshTodos();
                    setShowSignatureConfirm(false);
                    setShowNoteDetail(false);
                    // Reset signature state
                    setSignatureDate(new Date().toISOString());
                    setSignWithoutDate(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to sign note');
                  }
                }}
              >
                ✅ Mark as Completed (Non-billable)
              </Button>

              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={async () => {
                  try {
                    // Mar 2026 — Bug fix: supervisor sign was getting
                    // stuck on `sent_to_supervisor` because this branch
                    // explicitly re-asserted that status on the PUT,
                    // which short-circuited the backend's auto-advance
                    // at server.py L21064 (`if 'status' not in note_update`).
                    // For the PROVIDER, sending the note to the
                    // supervisor for review IS the next status, so we
                    // keep the explicit `sent_to_supervisor`.  For the
                    // SUPERVISOR, the next status is decided by the
                    // backend pipeline — `sent_to_client_for_signature`
                    // if the template requires client signature,
                    // `signed_ready_for_billing` if billable, otherwise
                    // `completed`.  Omitting `status` lets that pipeline
                    // run.  User report (Mar 2026): "when supervisor
                    // signs … it should automatically go to sent to
                    // client for signature, it seems to get stuck on
                    // sent to supervisor."
                    const signatureData = signatureAction === 'provider' ? {
                      provider_signed_at: signWithoutDate ? null : signatureDate,
                      provider_signed_by: user.id,
                      status: 'sent_to_supervisor'
                    } : {
                      supervisor_signed_at: signWithoutDate ? null : signatureDate,
                      supervisor_signed_by: user.id,
                      // No explicit status — backend auto-advances.
                    };

                    await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
                    toast.success(`Signed and sent for additional signatures`);
                    refreshNotes(); refreshTodos();
                    setShowSignatureConfirm(false);
                    setShowNoteDetail(false);
                    // Reset signature state
                    setSignatureDate(new Date().toISOString());
                    setSignWithoutDate(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to sign note');
                  }
                }}
              >
                🖊️ Send for Additional Signatures
              </Button>

              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={async () => {
                  try {
                    const signatureData = signatureAction === 'provider' ? {
                      provider_signed_at: signWithoutDate ? null : signatureDate,
                      provider_signed_by: user.id,
                      status: 'signed_ready_for_billing'
                    } : {
                      supervisor_signed_at: signWithoutDate ? null : signatureDate,
                      supervisor_signed_by: user.id,
                      status: 'signed_ready_for_billing'
                    };

                    // Optimistically update status and remove from tasks list
                    updateNoteStatusOptimistically(selectedNote.id, 'signed_ready_for_billing');

                    await axios.put(`${API}/notes/${selectedNote.id}`, signatureData);
                    toast.success(`Signed and sent to billing`);
                    refreshNotes(); refreshTodos();
                    setShowSignatureConfirm(false);
                    setShowNoteDetail(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to sign note');
                    refreshNotes(); refreshTodos(); // Revert on error
                  }
                }}
              >
                💰 Send to Billing
              </Button>
            </div>

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowSignatureConfirm(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};

/** Request Corrections Dialog. */
export const CorrectionsDialog = ({ ctx }) => {
  const {
    API,
    correctionsReason,
    refreshNotes,
    refreshTodos,
    selectedNote,
    setCorrectionsReason,
    setShowCorrectionsDialog,
    setShowNoteDetail,
    showCorrectionsDialog,
    user,
  } = ctx;

  return (
      <Dialog open={showCorrectionsDialog} onOpenChange={setShowCorrectionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Corrections</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please explain what corrections are needed for this note:
            </p>
            <DebouncedTextarea
              className="w-full min-h-[120px] p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g., Please fix the client data section and update the diagnosis..."
              value={correctionsReason}
              onChange={(e) => setCorrectionsReason(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCorrectionsDialog(false);
                  setCorrectionsReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                disabled={!correctionsReason.trim()}
                onClick={async () => {
                  try {
                    await axios.put(`${API}/notes/${selectedNote.id}`, {
                      status: 'needs_corrections',
                      needs_corrections_reason: correctionsReason,
                      corrections_requested_at: new Date().toISOString(),
                      corrections_requested_by: user.id
                    });
                    toast.success('Note marked as needing corrections');
                    refreshNotes(); refreshTodos();
                    setShowCorrectionsDialog(false);
                    setCorrectionsReason('');
                    setShowNoteDetail(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to update status');
                  }
                }}
              >
                Request Corrections
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
};

/** Mark Note as Non-Billable Dialog (reason + optional comment). */
export const NotBillableDialog = ({ ctx }) => {
  const {
    API,
    notBillableComment,
    notBillableReason,
    notBillableTargetNote,
    noteReviewData,
    refreshNotes,
    refreshTodos,
    setNotBillableComment,
    setNotBillableReason,
    setNotBillableTargetNote,
    setShowNotBillableDialog,
    setShowNoteReviewDialog,
    showNotBillableDialog,
    updateNoteStatusOptimistically,
  } = ctx;

  return (
          <Dialog
            open={showNotBillableDialog}
            onOpenChange={(open) => { setShowNotBillableDialog(open); if (!open) setNotBillableTargetNote(null); }}
          >
            <DialogContent className="max-w-md" data-testid="mark-not-billable-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-700">
                  <Ban className="h-5 w-5" />
                  Mark Note as Non-Billable
                </DialogTitle>
                <DialogDescription>
                  Records the note as terminally non-billable.  It leaves the
                  biller queue and the provider’s task list.  You can restore
                  it later if circumstances change (e.g., retro coverage).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold">Reason (required)</Label>
                  <Select value={notBillableReason} onValueChange={setNotBillableReason}>
                    <SelectTrigger className="mt-1" data-testid="not-billable-reason-select">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="duplicate_note">Duplicate of Another Note</SelectItem>
                      <SelectItem value="cpt_restricted_for_insurance">CPT Restricted for This Insurance</SelectItem>
                      <SelectItem value="timely_filing_expired">Timely Filing Expired</SelectItem>
                      <SelectItem value="no_active_insurance">No Active Insurance Coverage</SelectItem>
                      <SelectItem value="non_covered_service">Non-Covered Service</SelectItem>
                      <SelectItem value="missing_prior_authorization">Missing Prior Authorization</SelectItem>
                      <SelectItem value="administrative_writeoff">Administrative Write-off</SelectItem>
                      <SelectItem value="other">Other (see comment)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Comment (optional)</Label>
                  <Textarea
                    value={notBillableComment}
                    onChange={(e) => setNotBillableComment(e.target.value)}
                    placeholder="Optional context — internal note, who decided, special circumstances, etc."
                    rows={3}
                    className="mt-1"
                    data-testid="not-billable-comment-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setShowNotBillableDialog(false); setNotBillableTargetNote(null); }}
                  data-testid="not-billable-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!notBillableReason}
                  className="bg-slate-700 hover:bg-slate-800 text-white"
                  data-testid="not-billable-confirm-btn"
                  onClick={async () => {
                    const targetNote = notBillableTargetNote || noteReviewData?.note;
                    if (!targetNote?.id || !notBillableReason) return;
                    const additionalFields = {
                      not_billable_reason: notBillableReason,
                      not_billable_comment: notBillableComment.trim() || null,
                    };
                    try {
                      // Optimistic local update first — removes from
                      // both myTodoNotes (tasks) AND notesReadyForBilling
                      // immediately so the user sees the queue clean up.
                      // The helper handles both arrays correctly because
                      // 'not_billable' isn't in todoStatuses and isn't
                      // 'signed_ready_for_billing'.
                      updateNoteStatusOptimistically(targetNote.id, 'not_billable', additionalFields);

                      await axios.put(`${API}/notes/${targetNote.id}`, {
                        status: 'not_billable',
                        ...additionalFields,
                      });
                      toast.success(
                        notBillableReason === 'duplicate_note'
                          ? 'Note marked as duplicate.'
                          : 'Note marked non-billable.'
                      );
                      setShowNotBillableDialog(false);
                      setNotBillableTargetNote(null);
                      setShowNoteReviewDialog(false);
                      // Belt-and-braces refresh so the backend's filter
                      // for /api/my-todo + /api/notes overrides any
                      // stale local rows (e.g. a note that wasn't in
                      // notesReadyForBilling but was in myTodoNotes).
                      refreshNotes(); refreshTodos();
                    } catch (err) {
                      const detail = err.response?.data?.detail
                        || 'Failed to mark non-billable.';
                      toast.error(typeof detail === 'string' ? detail : 'Failed to mark non-billable.');
                      // Revert on failure
                      refreshNotes(); refreshTodos();
                    }
                  }}
                >
                  Mark Non-Billable
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
  );
};

/** Save-and-Close warning modal shown inside the create-note dialog. */
export const CloseNoteWarningModal = ({ ctx }) => {
  const {
    autosaveNoteId,
    handleCloseNoteDiscard,
    handleCloseNoteSaveAndClose,
    setShowCloseNoteWarning,
    showCloseNoteWarning,
  } = ctx;

  if (!showCloseNoteWarning) return null;

  return (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowCloseNoteWarning(false)}
              />
              {/* Modal */}
              <div className="relative bg-white rounded-lg shadow-2xl p-6 max-w-md mx-4 z-10">
                <h2 className="text-lg font-semibold mb-2">Unsaved Changes</h2>
                <p className="text-gray-600 mb-4">
                  {autosaveNoteId
                    ? "Your draft has been auto-saved and can be found in your Documents list. Would you like to save any final changes before closing?"
                    : "You have unsaved changes. What would you like to do?"}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowCloseNoteWarning(false)}
                  >
                    Keep Editing
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCloseNoteDiscard}
                  >
                    {autosaveNoteId ? "Close" : "Discard"}
                  </Button>
                  <Button
                    onClick={handleCloseNoteSaveAndClose}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    Save & Close
                  </Button>
                </div>
              </div>
            </div>
  );
};
